import { useMemo } from 'react'
import { useMonteCarloSimulation } from './useMonteCarloSimulation'
import { EquityChart } from './EquityChart'
import { ScoreGauge } from './ScoreGauge'
import { DistributionHistogram } from './DistributionHistogram'
import { MetricsComparison } from './MetricsComparison'
import { PercentileTable } from './PercentileTable'
import { COLORS, FONT_FAMILY } from './theme'
import type { MonteCarloDashboardProps, PercentileRow } from './types'

function generatePercentileRows(
  monteCarlo: MonteCarloDashboardProps['monteCarlo']
): PercentileRow[] {
  const percentiles = [0.01, 0.05, 0.1, 0.25, 0.5, 0.75, 0.9, 0.95, 0.99]
  const zScores = [-2.326, -1.645, -1.282, -0.674, 0, 0.674, 1.282, 1.645, 2.326]

  function interpolate(median: number, p5: number, p95: number, zP: number): number {
    const z5 = -1.645
    const z95 = 1.645
    const std = (p95 - p5) / (z95 - z5)
    return median + zP * std
  }

  const labels = ['1th', '5th', '10th', '25th', '50th', '75th', '90th', '95th', '99th']

  return percentiles.map((_, i) => ({
    percentile: labels[i],
    netProfit: interpolate(monteCarlo.netProfit.median, monteCarlo.netProfit.p5, monteCarlo.netProfit.p95, zScores[i]),
    maxDrawdown: interpolate(monteCarlo.maxDrawdown.median, monteCarlo.maxDrawdown.p5, monteCarlo.maxDrawdown.p95, zScores[i]),
    winRate: interpolate(monteCarlo.winRate.median, monteCarlo.winRate.p5, monteCarlo.winRate.p95, zScores[i]),
    profitFactor: Math.max(0, interpolate(monteCarlo.profitFactor.median, monteCarlo.profitFactor.p5, monteCarlo.profitFactor.p95, zScores[i])),
  }))
}

export function MonteCarloDashboard({
  asset,
  timeframe,
  historicalDays,
  simulations,
  totalTrades,
  backtest,
  monteCarlo,
}: MonteCarloDashboardProps) {
  const { loading, error, equityData, netProfitHistogram, maxDrawdownHistogram } = useMonteCarloSimulation({
    simulations,
    initialCapital: 10000,
    backtest,
    monteCarlo,
  })

  const percentileRows = useMemo(() => generatePercentileRows(monteCarlo), [monteCarlo])

  return (
    <div
      className="w-full p-4"
      style={{ backgroundColor: COLORS.background, fontFamily: FONT_FAMILY, minHeight: '700px' }}
    >
      <div className="text-center mb-4">
        <h2 className="text-[18px] font-bold tracking-wide" style={{ color: COLORS.primary }}>
          MONTE CARLO VALIDATION - {asset} {timeframe} Scanner
        </h2>
        <p className="text-[12px] mt-1" style={{ color: COLORS.textSecondary }}>
          {simulations.toLocaleString()} Simulaciones · {totalTrades} Trades · {historicalDays} Días Históricos
        </p>
      </div>

      {loading && (
        <div className="text-center py-8 text-[12px]" style={{ color: COLORS.textSecondary }}>
          Procesando simulaciones Monte Carlo en segundo plano...
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-[12px]" style={{ color: COLORS.secondary }}>
          Error: {error}
        </div>
      )}

      {!loading && !error && totalTrades === 0 && (
        <div
          className="p-6 border text-center"
          style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border }}
        >
          <p className="text-[14px] font-bold mb-2" style={{ color: COLORS.warning }}>
            No se generaron trades en la simulación
          </p>
          <p className="text-[12px]" style={{ color: COLORS.textSecondary }}>
            El scanner no produjo señales de entrada durante el periodo analizado. Prueba con más días históricos,
            otros activos/timeframes o revisa la configuración de los indicadores.
          </p>
        </div>
      )}

      {!loading && !error && totalTrades > 0 && (
        <div
          className="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-[2fr_1fr] gap-4"
          style={{ height: '700px', minHeight: '600px' }}
        >
          <div
            className="col-span-1 lg:col-span-8 p-4 border h-full min-h-[300px] overflow-hidden flex flex-col"
            style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border, borderRadius: '0px' }}
          >
            <EquityChart data={equityData} />
          </div>

          <div
            className="col-span-1 lg:col-span-4 p-4 border h-full min-h-[300px] overflow-hidden flex flex-col"
            style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border, borderRadius: '0px' }}
          >
            <ScoreGauge score={monteCarlo.score} scoreLabel={monteCarlo.scoreLabel} status={monteCarlo.status} />
          </div>

          <div
            className="col-span-1 lg:col-span-3 p-4 border h-full min-h-[220px] overflow-hidden flex flex-col"
            style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border, borderRadius: '0px' }}
          >
            {maxDrawdownHistogram && (
              <DistributionHistogram
                data={maxDrawdownHistogram}
                title="Distribución Max Drawdown"
                xLabel="Max Drawdown (%)"
                mode="drawdown"
              />
            )}
          </div>

          <div
            className="col-span-1 lg:col-span-3 p-4 border h-full min-h-[220px] overflow-hidden flex flex-col"
            style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border, borderRadius: '0px' }}
          >
            <MetricsComparison backtest={backtest} monteCarlo={monteCarlo} />
          </div>

          <div
            className="col-span-1 lg:col-span-3 p-4 border h-full min-h-[220px] overflow-hidden flex flex-col"
            style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border, borderRadius: '0px' }}
          >
            {netProfitHistogram && (
              <DistributionHistogram
                data={netProfitHistogram}
                title="Distribución Net Profit"
                xLabel="Net Profit (%)"
                mode="profit"
              />
            )}
          </div>

          <div
            className="col-span-1 lg:col-span-3 p-4 border h-full min-h-[220px] overflow-hidden flex flex-col"
            style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border, borderRadius: '0px' }}
          >
            <PercentileTable rows={percentileRows} />
          </div>
        </div>
      )}
    </div>
  )
}
